import type { FmRiskWorkspacePageDescriptor } from "./FmRiskWorkspaceRoot.types";

function exactKeys(
  value: object,
  allowed: readonly string[],
  path: string,
): void {
  const unexpected = Object.keys(value).find((key) => !allowed.includes(key));
  if (unexpected) {
    throw new Error(
      `Invalid risk workspace descriptor: ${path}.${unexpected} is not allowed.`,
    );
  }
}

function plainText(value: unknown, path: string): asserts value is string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(
      `Invalid risk workspace descriptor: ${path} must be non-empty plain text.`,
    );
  }
}

function action(
  value: { id: string; label: string; onSelect: () => void },
  path: string,
  allowLoading = false,
): void {
  exactKeys(
    value,
    allowLoading
      ? ["id", "label", "loading", "onSelect"]
      : ["id", "label", "onSelect"],
    path,
  );
  plainText(value.id, `${path}.id`);
  plainText(value.label, `${path}.label`);
  if (typeof value.onSelect !== "function") {
    throw new Error(
      `Invalid risk workspace descriptor: ${path}.onSelect must be a callback.`,
    );
  }
}

/**
 * An alert row prints its title, hazard, meta and freshness on adjacent lines,
 * so a hazard that the title already names reads twice.
 */
function assertRowNamesHazardOnce(
  row: { title: string; hazard?: string; meta?: string },
  index: number,
): void {
  const hazard = row.hazard?.trim().toLocaleLowerCase();
  if (!hazard) return;
  const repeats = [row.title, row.meta].find((text) =>
    text?.toLocaleLowerCase().includes(hazard),
  );
  if (repeats !== undefined) {
    throw new Error(
      `Invalid risk workspace descriptor: workspace.rows[${index}] names its hazard "${row.hazard}" again in "${repeats}"; the hazard line states it once.`,
    );
  }
}

export function validateRiskWorkspacePageDescriptor(
  descriptor: FmRiskWorkspacePageDescriptor,
): void {
  plainText(descriptor.kind, "kind");
  if (
    !["surface", "locked-preview", "arm", "workspace"].includes(descriptor.kind)
  ) {
    throw new Error(
      `Invalid risk workspace descriptor: kind is not registered: "${String(descriptor.kind)}".`,
    );
  }
  exactKeys(
    descriptor,
    descriptor.kind === "surface"
      ? [
          "kind",
          "notice",
          "testId",
          "title",
          "body",
          "description",
          "action",
          "state",
        ]
      : descriptor.kind === "locked-preview"
        ? [
            "kind",
            "notice",
            "testId",
            "eyebrow",
            "title",
            "body",
            "bullets",
            "assetKey",
            "sampleTitle",
            "sampleBody",
            "unlock",
          ]
        : descriptor.kind === "arm"
          ? [
              "kind",
              "notice",
              "testId",
              "eyebrow",
              "title",
              "body",
              "coverage",
              "action",
              "error",
            ]
          : ["kind", "notice", "testId", "workspace", "mapSnapshot"],
    "root",
  );
  if (descriptor.testId !== undefined) plainText(descriptor.testId, "testId");
  if (descriptor.notice) {
    exactKeys(
      descriptor.notice,
      ["title", "description", "action", "testId"],
      "notice",
    );
    plainText(descriptor.notice.title, "notice.title");
    plainText(descriptor.notice.description, "notice.description");
    plainText(descriptor.notice.testId, "notice.testId");
    if (descriptor.notice.action)
      action(descriptor.notice.action, "notice.action");
  }
  if (descriptor.kind === "workspace") {
    exactKeys(
      descriptor.workspace,
      [
        "title",
        "eyebrow",
        "tone",
        "status",
        "freshness",
        "evidence",
        "metrics",
        "filters",
        "filterLabel",
        "filtersMoreLabel",
        "suppression",
        "banners",
        "map",
        "listTitle",
        "rows",
        "selectedId",
        "onSelectRow",
        "listEmptyLabel",
        "detail",
        "detailTitle",
        "detailEmptyLabel",
        "labels",
        "timeline",
        "timelineTitle",
        "loading",
        "errorLabel",
        "loadingLabel",
      ],
      "workspace",
    );
    plainText(descriptor.workspace.title, "workspace.title");
    descriptor.workspace.rows?.forEach((row, index) => assertRowNamesHazardOnce(row, index));
    return;
  }
  plainText(descriptor.title, `${descriptor.kind}.title`);
  plainText(descriptor.body, `${descriptor.kind}.body`);
  if (descriptor.kind === "surface") {
    if (!["empty", "loading", "error"].includes(descriptor.state)) {
      throw new Error(
        "Invalid risk workspace descriptor: surface.state is invalid.",
      );
    }
    if (descriptor.action) action(descriptor.action, "surface.action");
    return;
  }
  plainText(descriptor.eyebrow, `${descriptor.kind}.eyebrow`);
  if (descriptor.kind === "arm") {
    plainText(descriptor.coverage, "arm.coverage");
    action(descriptor.action, "arm.action", true);
    return;
  }
  exactKeys(
    descriptor.unlock,
    ["title", "summary", "priceLabel", "actionId", "actionLabel", "onSelect"],
    "locked-preview.unlock",
  );
  plainText(descriptor.assetKey, "locked-preview.assetKey");
  if (descriptor.bullets.length !== 3) {
    throw new Error(
      "Invalid risk workspace descriptor: locked-preview.bullets must contain exactly three messages.",
    );
  }
  descriptor.bullets.forEach((bullet, index) =>
    plainText(bullet, `locked-preview.bullets[${index}]`),
  );
  plainText(descriptor.sampleTitle, "locked-preview.sampleTitle");
  plainText(descriptor.sampleBody, "locked-preview.sampleBody");
  plainText(descriptor.unlock.title, "locked-preview.unlock.title");
  plainText(descriptor.unlock.summary, "locked-preview.unlock.summary");
  plainText(descriptor.unlock.priceLabel, "locked-preview.unlock.priceLabel");
  action(
    {
      id: descriptor.unlock.actionId,
      label: descriptor.unlock.actionLabel,
      onSelect: descriptor.unlock.onSelect,
    },
    "locked-preview.unlock",
  );
}
